import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/apiService';

// Página admin: o log de acesso do MCP — uma linha por chamada de tool, incluindo LEITURA e
// tentativa RECUSADA. Consome /api/mcp/audit, que passa pelo apidash e lê o CloudWatch.
//
// Por que o CloudWatch e não a tabela: as duas `internal_mcp.tool_calls` moram em redes
// diferentes (a de prod é Postgres local da EC2 do apidash; a de QA está no RDS de QA, em
// outra VPC) e nenhuma caixa alcança as duas. No CloudWatch os dois log groups convivem.
//
// A busca livre (`contem`) filtra a PÁGINA já trazida, não o log inteiro — o FilterLogEvents
// do CloudWatch não faz busca de texto. Está dito na tela para ninguém concluir "não houve
// atividade" a partir de uma busca que só olhou 100 linhas.

// O nome diz QUAL SERVIDOR, não em que ambiente ele roda: são três MCPs diferentes, e dois
// deles atendem produção. Chamar um de "Produção" fazia parecer que o de clientes era um
// ambiente de teste do mesmo servidor.
const ENVS = [
  { id: 'prod', nome: 'MCP Interno', cor: '#b91c1c' },
  { id: 'qa', nome: 'MCP Interno QA', cor: '#2563eb' },
  // O único cujo log traz `pergunta`, `empresa` e `operador`: as tools dele pedem a
  // pergunta em linguagem de gente, as dos internos não têm o campo.
  { id: 'cliente', nome: 'MCP Clientes', cor: '#7c3aed' },
];

const JANELAS = [
  { label: 'última hora', min: 60 },
  { label: '24 horas', min: 60 * 24 },
  { label: '7 dias', min: 60 * 24 * 7 },
  { label: '30 dias', min: 60 * 24 * 30 },
];

/**
 * Instante -> horário de Brasília. SEMPRE BRT, independente do fuso de quem abre a tela:
 * quem lê isto está falando com gente que vive no fuso do Brasil, e um log em UTC (ou no
 * fuso do notebook de quem viajou) faz duas pessoas discutirem sobre horas diferentes.
 *
 * Duas defesas, as duas pagas na prática:
 *
 * - O CloudWatch manda o instante em DOIS formatos, e os dois enganam o `new Date()`:
 *   `2026-09-22 19:52:55.579` (sem marca de fuso — o V8 lê como hora local, o Safari devolve
 *   Invalid Date) e `1790107826000`, epoch em milissegundos, que é o que `latest(@timestamp)`
 *   devolve de fato. O backend normaliza os dois (`mcp_admin.py:_iso_utc`) e aqui fazemos de
 *   novo, para a tela não depender de qual versão da API está no ar.
 * - Data inválida vira o valor CRU, não "Invalid Date". Mostrar o que veio deixa consertar;
 *   "Invalid Date" só diz que alguém errou, sem dizer o quê.
 */
function fmtHora(iso) {
  if (!iso) return '—';
  const bruto = String(iso).trim();
  let d;
  if (/^\d{10}$|^\d{13}$/.test(bruto)) {
    d = new Date(Number(bruto) * (bruto.length === 10 ? 1000 : 1));
  } else {
    const normalizado = /([Zz]|[+-]\d{2}:?\d{2})$/.test(bruto)
      ? bruto
      : bruto.replace(' ', 'T') + 'Z';
    d = new Date(normalizado);
  }
  if (isNaN(d.getTime())) return bruto;
  return d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

export default function McpAuditoria() {
  const [env, setEnv] = useState('prod');
  const [minutos, setMinutos] = useState(60 * 24);
  const [email, setEmail] = useState('');
  const [tool, setTool] = useState('');
  const [soRecusadas, setSoRecusadas] = useState(false);
  const [soFalhas, setSoFalhas] = useState(false);
  const [contem, setContem] = useState('');

  const [eventos, setEventos] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const [erro, setErro] = useState(null);
  const [aberto, setAberto] = useState(null);
  // Três perguntas diferentes sobre o mesmo log, e é a separação que faz a segunda e a
  // terceira existirem: juntas numa lista só, "o que pediram e não fizemos" some no meio
  // do que deu certo.
  const [aba, setAba] = useState('chamadas');
  const [uso, setUso] = useState(null);
  const [lacunas, setLacunas] = useState(null);

  const params = useCallback((extra = {}) => ({
    env, minutos, limit: 100,
    ...(email ? { email } : {}), ...(tool ? { tool } : {}),
    ...(soRecusadas ? { only_denied: true } : {}),
    ...(soFalhas ? { only_errors: true } : {}),
    ...(contem ? { contem } : {}),
    ...extra,
  }), [env, minutos, email, tool, soRecusadas, soFalhas, contem]);

  const buscar = useCallback(async () => {
    setLoading(true); setErro(null); setAberto(null);
    try {
      const d = await apiService.getMcpAudit(params());
      // Resposta fora de forma é erro, não dado — ver o comentário em McpPermissoes.
      if (!d || !Array.isArray(d.eventos)) {
        throw new Error('resposta inesperada do servidor (rota do BFF registrada?)');
      }
      setEventos(d.eventos); setCursor(d.cursor || null);
    } catch (e) {
      setErro(e?.response?.data?.error || e.message || 'falha ao carregar');
      setEventos([]); setCursor(null);
    } finally { setLoading(false); }
  }, [params]);

  const mais = async () => {
    if (!cursor) return;
    setCarregandoMais(true);
    try {
      const d = await apiService.getMcpAudit(params({ cursor }));
      setEventos(prev => [...prev, ...(d.eventos || [])]);
      setCursor(d.cursor || null);
    } catch (e) {
      setErro(e?.response?.data?.error || e.message);
    } finally { setCarregandoMais(false); }
  };

  const verUso = async () => {
    setUso({ carregando: true });
    try { setUso(await apiService.getMcpUso({ env, minutos })); }
    catch (e) { setUso({ erro: e?.response?.data?.error || e.message }); }
  };

  const verLacunas = async () => {
    setLacunas({ carregando: true });
    try { setLacunas(await apiService.getMcpLacunas({ env, minutos })); }
    catch (e) { setLacunas({ erro: e?.response?.data?.error || e.message }); }
  };

  // O resumo é uma chamada SEPARADA de propósito: ela roda no Logs Insights, que é
  // assíncrono e cobra por GB varrido. Prendê-la à listagem faria toda mudança de filtro
  // custar uma consulta paga.
  const verResumo = async () => {
    setResumo({ carregando: true });
    try { setResumo(await apiService.getMcpAuditResumo({ env, minutos })); }
    catch (e) { setResumo({ erro: e?.response?.data?.error || e.message }); }
  };

  // `buscar` fica FORA das dependências de propósito. Ela é recriada a cada tecla digitada
  // nos campos de texto (e-mail, tool, contém), e incluí-la dispararia uma consulta ao
  // CloudWatch por caractere — que é chamada paga e lenta. Os campos de texto valem no
  // Enter ou no botão; só os controles discretos abaixo recarregam sozinhos.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { buscar(); }, [env, minutos, soRecusadas, soFalhas]);

  const S = {
    // Sem teto de largura: com Pergunta, Empresa e Argumentos na mesma linha, 1280px
    // espremia tudo em reticências. Esta tela é para LER texto, não para caber.
    wrap: { maxWidth: '100%', margin: '0 auto', padding: '1.5rem' },
    h1: { fontSize: 22, margin: 0 },
    sub: { color: '#666', fontSize: 13, marginTop: 4 },
    card: { background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: 16, marginTop: 16 },
    filtros: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 },
    input: { padding: '7px 10px', borderRadius: 8, border: '1px solid #ccc', fontSize: 13 },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: 12 },
    th: { textAlign: 'left', padding: '9px 10px', borderBottom: '2px solid #eee', fontSize: 12, color: '#555' },
    td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0', fontSize: 13, verticalAlign: 'top' },
    btn: { padding: '6px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13 },
    tag: { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11 },
    erro: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#991b1b' },
    mono: { fontFamily: 'ui-monospace,monospace', fontSize: 12 },
    pre: { fontFamily: 'ui-monospace,monospace', fontSize: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 320, overflow: 'auto' },
  };

  const selo = (e) => {
    if (e.denied) return <span style={{ ...S.tag, background: '#fee2e2', color: '#991b1b' }}>recusada</span>;
    if (e.ok === false) return <span style={{ ...S.tag, background: '#fef3c7', color: '#92400e' }}>erro</span>;
    return <span style={{ ...S.tag, background: '#dcfce7', color: '#166534' }}>ok</span>;
  };

  // Colunas que só existem em UM ambiente aparecem só quando há dado delas. Nos MCPs
  // internos `empresa` é sempre nula e `pergunta` também (as 48 tools de lá não têm esse
  // argumento) — e uma coluna de traços em todas as linhas parece defeito da coluna, não
  // ausência do campo.
  const temEmpresa = eventos.some(e => e.empresa);
  const temPergunta = eventos.some(e => e.pergunta);

  return (
    <div style={S.wrap}>
      <h1 style={S.h1}>Logs de Auditoria do MCP</h1>
      <div style={S.sub}>
        Uma linha por chamada de tool — leitura, escrita e tentativa recusada. Vem do
        CloudWatch (<span style={S.mono}>/aliado/mcp/{env}</span>), que é onde os dois
        ambientes convivem.
      </div>

      <div style={S.filtros}>
        {ENVS.map(x => (
          <button key={x.id} onClick={() => setEnv(x.id)}
                  style={{ ...S.btn, ...(env === x.id ? { background: x.cor, color: '#fff', borderColor: x.cor } : {}) }}>
            {x.nome}
          </button>
        ))}
        <select style={S.input} value={minutos} onChange={e => setMinutos(Number(e.target.value))}>
          {JANELAS.map(j => <option key={j.min} value={j.min}>{j.label}</option>)}
        </select>
        <input style={S.input} placeholder="e-mail" value={email}
               onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && buscar()} />
        <input style={S.input} placeholder="tool" value={tool}
               onChange={e => setTool(e.target.value)} onKeyDown={e => e.key === 'Enter' && buscar()} />
        <input style={{ ...S.input, width: 200 }} placeholder="contém (nesta página)" value={contem}
               onChange={e => setContem(e.target.value)} onKeyDown={e => e.key === 'Enter' && buscar()} />
        <label style={{ fontSize: 13 }}>
          <input type="checkbox" checked={soRecusadas} onChange={e => setSoRecusadas(e.target.checked)} />
          {' '}só recusadas
        </label>
        <label style={{ fontSize: 13 }}>
          <input type="checkbox" checked={soFalhas} onChange={e => setSoFalhas(e.target.checked)} />
          {' '}só com erro
        </label>
        <button style={S.btn} onClick={buscar}>Buscar</button>
        <button style={S.btn} onClick={verResumo}>Resumo por pessoa</button>
      </div>

      {/* Três perguntas diferentes sobre o mesmo log. Separadas de propósito: numa lista só,
          "o que pediram e não fizemos" some no meio do que deu certo. */}
      <div style={{ display: 'flex', gap: 8, marginTop: 16, borderBottom: '1px solid #eee' }}>
        {[['chamadas', 'Chamadas'], ['uso', 'Uso'], ['lacunas', 'Lacunas']].map(([id, nome]) => (
          <button key={id} onClick={() => {
                    setAba(id);
                    if (id === 'uso' && !uso) verUso();
                    if (id === 'lacunas' && !lacunas) verLacunas();
                  }}
                  style={{ ...S.btn, border: 'none', borderRadius: 0, padding: '8px 14px',
                           borderBottom: aba === id ? '2px solid #111' : '2px solid transparent',
                           fontWeight: aba === id ? 600 : 400 }}>
            {nome}
          </button>
        ))}
      </div>

      {aba === 'chamadas' && (<>

      {resumo && (
        <div style={S.card}>
          <strong>Resumo por pessoa</strong>
          <span style={S.sub}>{'  '}janela selecionada · roda no Logs Insights</span>
          {resumo.carregando ? <div style={S.sub}>Consultando…</div>
            : resumo.erro ? <div style={{ ...S.erro, marginTop: 8 }}>{resumo.erro}</div>
            : (
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Pessoa</th><th style={S.th}>Chamadas</th>
                  <th style={S.th}>Falhas</th><th style={S.th}>Recusadas</th><th style={S.th}>Última</th>
                </tr></thead>
                <tbody>
                  {(resumo.por_pessoa || []).map(r => (
                    <tr key={r.email}>
                      <td style={{ ...S.td, ...S.mono }}>{r.email}</td>
                      <td style={S.td}>{r.chamadas}</td>
                      <td style={S.td}>{r.falhas}</td>
                      <td style={S.td}>{r.barradas}</td>
                      <td style={S.td}>{fmtHora(r.ultima)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      )}

      <div style={S.card}>
        {loading ? <div>Carregando…</div>
          : erro ? <div style={S.erro}>Erro: {erro}</div>
          : eventos.length === 0 ? <div style={S.sub}>Nenhuma chamada nesta janela com estes filtros.</div>
          : (
            <>
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Quando</th><th style={S.th}>Pessoa</th>
                  {/* Só no servidor de clientes: nos internos a coluna sairia sempre vazia. */}
                  {temEmpresa && <th style={S.th}>Empresa</th>}
                  <th style={S.th}>Tool</th>
                  {/* A PERGUNTA é o insumo de produto desta tela: é o pedido em linguagem de
                      gente, e é ela que diz o que falta no servidor. Vem antes dos argumentos
                      de propósito — argumento é o "como", pergunta é o "o quê". */}
                  {temPergunta && <th style={S.th}>Pergunta</th>}
                  <th style={S.th}>Estado</th><th style={S.th}>Dur.</th><th style={S.th}>Linhas</th>
                  <th style={S.th}>Argumentos</th>
                </tr></thead>
                <tbody>
                  {eventos.map((e, i) => {
                    const chave = `${e.ts}-${i}`;
                    const args = JSON.stringify(e.args || {}, null, 2);
                    return (
                      <React.Fragment key={chave}>
                        <tr style={{ cursor: 'pointer' }}
                            onClick={() => setAberto(aberto === chave ? null : chave)}>
                          <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtHora(e.ts)}</td>
                          <td style={{ ...S.td, ...S.mono }}>
                            {e.email || '—'}
                            {e.operador
                              ? <span style={{ ...S.tag, marginLeft: 6, background: '#e0e7ff', color: '#3730a3' }}
                                      title="gente da Aliado lendo o dado de um cliente, não uma pessoa do cliente">Aliado</span>
                              : null}
                          </td>
                          {temEmpresa && <td style={{ ...S.td, ...S.mono }}>{e.empresa || '—'}</td>}
                          <td style={{ ...S.td, ...S.mono }}>{e.tool}</td>
                          {temPergunta && (
                            <td style={{ ...S.td, minWidth: 260, maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                title={e.pergunta || ''}>
                              {e.pergunta || '—'}
                            </td>
                          )}
                          <td style={S.td}>{selo(e)}</td>
                          <td style={S.td}>{e.duration_ms != null ? `${e.duration_ms}ms` : '—'}</td>
                          <td style={S.td}>{e.result_rows != null ? e.result_rows : '—'}</td>
                          <td style={{ ...S.td, ...S.mono, maxWidth: 560, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {(e.args?.sql || args).slice(0, 180)}
                          </td>
                        </tr>
                        {aberto === chave && (
                          <tr><td style={S.td} colSpan={7 + (temEmpresa ? 1 : 0) + (temPergunta ? 1 : 0)}>
                            {e.error ? <div style={{ ...S.erro, marginBottom: 8 }}>{e.error_type}: {e.error}</div> : null}
                            <div style={S.pre}>{args}</div>
                            <div style={{ ...S.sub, marginTop: 6 }}>
                              sub {e.sub || '—'} · request {e.request_id || '—'} ·
                              {' '}sessão {e.sessao_id ? e.sessao_id.slice(0, 8) : '—'} ·
                              {' '}retorno {e.result_bytes != null ? `${e.result_bytes} bytes` : '—'}
                              {e.empresa ? ` · empresa ${e.empresa}` : ''}
                              {e.papel ? ` · papel ${e.papel}` : ''}
                              {e.tool_desconhecida ? ' · PEDIU O QUE NÃO EXISTE' : ''}
                            </div>
                          </td></tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>

              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
                <span style={S.sub}>{eventos.length} linha(s) carregada(s)</span>
                {cursor && (
                  <button style={S.btn} onClick={mais} disabled={carregandoMais}>
                    {carregandoMais ? 'Carregando…' : 'Carregar mais'}
                  </button>
                )}
              </div>
              {contem ? (
                <div style={{ ...S.sub, marginTop: 8 }}>
                  <strong>Atenção:</strong> “contém” filtra apenas as linhas já carregadas —
                  o CloudWatch não faz busca de texto livre. Para varrer a janela inteira,
                  use os filtros de e-mail e tool.
                </div>
              ) : null}
            </>
          )}
      </div>

      </>)}

      {aba === 'uso' && (
        <div style={S.card}>
          <strong>Uso</strong>
          <span style={S.sub}>{'  '}quem usa, o quê, e em que sequência</span>
          <button style={{ ...S.btn, marginLeft: 12 }} onClick={verUso}>Atualizar</button>
          {!uso || uso.carregando ? <div style={S.sub}>Consultando…</div>
            : uso.erro ? <div style={{ ...S.erro, marginTop: 8 }}>{uso.erro}</div>
            : (<>
              <div style={{ ...S.sub, marginTop: 14, fontWeight: 600 }}>Por consulta</div>
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Tool</th><th style={S.th}>Chamadas</th>
                  <th style={S.th}>Pessoas</th><th style={S.th}>Recusadas</th><th style={S.th}>Falhas</th>
                </tr></thead>
                <tbody>{(uso.por_tool || []).map(r => (
                  <tr key={r.tool}>
                    <td style={{ ...S.td, ...S.mono }}>{r.tool}</td>
                    <td style={S.td}>{r.chamadas}</td><td style={S.td}>{r.pessoas}</td>
                    <td style={S.td}>{r.recusadas}</td><td style={S.td}>{r.falhas}</td>
                  </tr>))}</tbody>
              </table>

              {(uso.por_empresa || []).length > 0 && (<>
                <div style={{ ...S.sub, marginTop: 20, fontWeight: 600 }}>Por empresa</div>
                {/* Pessoas do cliente e da Aliado em colunas separadas: somá-las infla o
                    número de adoção que alguém vai levar para uma reunião. */}
                <table style={S.table}>
                  <thead><tr>
                    <th style={S.th}>Empresa</th><th style={S.th}>Chamadas</th>
                    <th style={S.th}>Pessoas</th><th style={S.th}>Conversas</th>
                    <th style={S.th}>Chamadas da Aliado</th>
                  </tr></thead>
                  <tbody>{uso.por_empresa.map(r => (
                    <tr key={r.empresa}>
                      <td style={{ ...S.td, ...S.mono }}>{r.empresa}</td>
                      <td style={S.td}>{r.chamadas}</td><td style={S.td}>{r.pessoas}</td>
                      <td style={S.td}>{r.conversas}</td>
                      <td style={S.td}>{r.chamadas_da_aliado}</td>
                    </tr>))}</tbody>
                </table>
              </>)}

              {(uso.sessoes || []).length > 0 && (<>
                <div style={{ ...S.sub, marginTop: 20, fontWeight: 600 }}>Conversas mais longas</div>
                <div style={S.sub}>
                  A sequência é o que mostra <em>como</em> se usa: onde a conversa começa e
                  onde termina diz mais que a contagem de chamadas.
                </div>
                <table style={S.table}>
                  <thead><tr>
                    <th style={S.th}>Pessoa</th><th style={S.th}>Empresa</th>
                    <th style={S.th}>Chamadas</th><th style={S.th}>Começou em</th>
                    <th style={S.th}>Terminou em</th>
                  </tr></thead>
                  <tbody>{uso.sessoes.map(r => (
                    <tr key={r.sessao_id}>
                      <td style={{ ...S.td, ...S.mono }}>{r.pessoa || '—'}</td>
                      <td style={{ ...S.td, ...S.mono }}>{r.cliente || '—'}</td>
                      <td style={S.td}>{r.chamadas}</td>
                      <td style={{ ...S.td, ...S.mono }}>{r.primeira}</td>
                      <td style={{ ...S.td, ...S.mono }}>{r.ultima}</td>
                    </tr>))}</tbody>
                </table>
              </>)}
            </>)}
        </div>
      )}

      {aba === 'lacunas' && (
        <div style={S.card}>
          <strong>Lacunas</strong>
          <span style={S.sub}>{'  '}o que pediram e o servidor não fez</span>
          <button style={{ ...S.btn, marginLeft: 12 }} onClick={verLacunas}>Atualizar</button>
          {!lacunas || lacunas.carregando ? <div style={S.sub}>Consultando…</div>
            : lacunas.erro ? <div style={{ ...S.erro, marginTop: 8 }}>{lacunas.erro}</div>
            : (lacunas.lacunas || []).length === 0
              ? <div style={{ ...S.sub, marginTop: 10 }}>Nada nesta janela — ninguém esbarrou em limite.</div>
            : (<>
              <div style={{ ...S.sub, marginTop: 8 }}>
                <strong>A pergunta é escrita pelo modelo</strong>, a partir do pedido da
                pessoa. Serve para decidir o que construir — não é registro fiel do que foi dito.
              </div>
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Quando</th><th style={S.th}>Motivo</th>
                  <th style={S.th}>Pessoa</th><th style={S.th}>Empresa</th>
                  <th style={S.th}>Pediu</th><th style={S.th}>Pergunta</th>
                </tr></thead>
                <tbody>{lacunas.lacunas.map((r, i) => (
                  <tr key={`${r.ts}-${i}`}>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtHora(r.ts)}</td>
                    <td style={S.td}>
                      <span style={{ ...S.tag, background: r.motivo === 'pediu o que não existe' ? '#fef3c7' : '#f3f4f6',
                                     color: r.motivo === 'pediu o que não existe' ? '#92400e' : '#374151' }}>
                        {r.motivo}
                      </span>
                    </td>
                    <td style={{ ...S.td, ...S.mono }}>
                      {r.email || '—'}
                      {r.operador === '1' ? <span style={{ ...S.tag, marginLeft: 6, background: '#e0e7ff', color: '#3730a3' }}>Aliado</span> : null}
                    </td>
                    <td style={{ ...S.td, ...S.mono }}>{r.empresa || '—'}</td>
                    {/* O nome que o modelo INVENTOU é o pedido dito na linguagem de quem
                        pediu — por isso ele aparece em destaque, e não escondido no erro. */}
                    <td style={{ ...S.td, ...S.mono }}>{r.tool}</td>
                    <td style={{ ...S.td, minWidth: 280 }} title={r.pergunta || ''}>{r.pergunta || '—'}</td>
                  </tr>))}</tbody>
              </table>
            </>)}
        </div>
      )}

      <div style={{ ...S.sub, marginTop: 12 }}>
        O resultado da tool nunca é registrado — só o tamanho e a contagem de linhas. Senhas
        são redigidas por nome de argumento antes de sair do servidor.
      </div>
    </div>
  );
}
